import { matchesLocalizedSearch } from '@/i18n/search';
// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from '@/i18n/router';
import { featuredProducts as mockProducts } from '../../mocks/products';
import { bundles as mockBundles } from '../../mocks/bundles';
import { getProductSlug } from '../../utils/slugify';
import ProductImage from '../base/ProductImage';
import { supabaseClient, shouldUseSupabase } from '../../lib/supabaseClient';
import { gaSearch } from '@/utils/analytics';

interface SearchResult {
  id: number;
  name: string;
  price: string;
  image: string;
  category: string;
  type: 'product' | 'bundle';
  slug: string;
  isOnSale: boolean;
  originalPrice?: string;
}

interface SearchAutocompleteProps {
  isOpen: boolean;
  onClose: () => void;
  isDark?: boolean;
}

// Normaliza texto para búsqueda sin tildes
const normalize = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const SearchAutocomplete = ({ isOpen, onClose, isDark: _isDark = false }: SearchAutocompleteProps) => {
  const language = useStoreLanguage();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  // Cache de todos los items para búsqueda local rápida
  const [allItems, setAllItems] = useState<SearchResult[]>([]);
  const [_itemsLoaded, setItemsLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const popularCategories = [
    { label: 'Especias', icon: 'ri-leaf-line' },
    { label: 'Hierbas', icon: 'ri-plant-line' },
    { label: 'Mezclas', icon: 'ri-flask-line' },
    { label: 'Sets', icon: 'ri-gift-line' },
    { label: 'Packs', icon: 'ri-stack-line' },
  ];

  // Cargar todos los items (productos + bundles) desde Supabase o mock
  useEffect(() => {
    const loadItems = async () => {
      if (!shouldUseSupabase() || !supabaseClient) {
        // Fallback a mock
        const productItems: SearchResult[] = mockProducts.map((p) => ({
          id: p.id,
          name: p.name,
          price: p.price,
          image: p.image,
          category: p.category,
          type: 'product',
          slug: `/product/${getProductSlug(p.name)}`,
          isOnSale: p.isOnSale,
          originalPrice: p.originalPrice,
        }));
        const bundleItems: SearchResult[] = mockBundles.map((b) => ({
          id: b.id,
          name: b.name,
          price: b.bundlePrice,
          image: b.image,
          category: 'Pack',
          type: 'bundle',
          slug: `/bundle/${b.slug}`,
          isOnSale: true,
          originalPrice: b.originalPrice,
        }));
        setAllItems([...productItems, ...bundleItems]);
        setItemsLoaded(true);
        return;
      }

      try {
        const [productsRes, bundlesRes] = await Promise.all([
          supabaseClient.from('products').select('id, name, slug, price, original_price, image, category, is_on_sale').eq('is_active', true),
          supabaseClient.from('bundles').select('id, name, bundle_price, original_price, image, slug'),
        ]);

        const productItems: SearchResult[] = (productsRes.data || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          price: Number(p.price).toFixed(2).replace('.', ','),
          image: p.image || '',
          category: p.category || '',
          type: 'product',
          slug: `/product/${p.slug || getProductSlug(p.name)}`,
          isOnSale: Boolean(p.is_on_sale),
          originalPrice: p.original_price ? Number(p.original_price).toFixed(2).replace('.', ',') : undefined,
        }));

        const bundleItems: SearchResult[] = (bundlesRes.data || []).map((b: any) => ({
          id: b.id,
          name: b.name,
          price: Number(b.bundle_price).toFixed(2).replace('.', ','),
          image: b.image || '',
          category: 'Pack',
          type: 'bundle',
          slug: `/bundle/${b.slug}`,
          isOnSale: true,
          originalPrice: b.original_price ? Number(b.original_price).toFixed(2).replace('.', ',') : undefined,
        }));

        const combined = [...productItems, ...bundleItems];
        setAllItems(combined.length > 0 ? combined : [
          ...mockProducts.map((p) => ({
            id: p.id, name: p.name, price: p.price, image: p.image, category: p.category,
            type: 'product' as const, slug: `/product/${getProductSlug(p.name)}`,
            isOnSale: p.isOnSale, originalPrice: p.originalPrice,
          })),
          ...mockBundles.map((b) => ({
            id: b.id, name: b.name, price: b.bundlePrice, image: b.image, category: 'Pack',
            type: 'bundle' as const, slug: `/bundle/${b.slug}`,
            isOnSale: true, originalPrice: b.originalPrice,
          })),
        ]);
      } catch {
        // Fallback a mock en caso de error — silencioso, sin console.error
        setAllItems([
          ...mockProducts.map((p) => ({
            id: p.id, name: p.name, price: p.price, image: p.image, category: p.category,
            type: 'product' as const, slug: `/product/${getProductSlug(p.name)}`,
            isOnSale: p.isOnSale, originalPrice: p.originalPrice,
          })),
          ...mockBundles.map((b) => ({
            id: b.id, name: b.name, price: b.bundlePrice, image: b.image, category: 'Pack',
            type: 'bundle' as const, slug: `/bundle/${b.slug}`,
            isOnSale: true, originalPrice: b.originalPrice,
          })),
        ]);
      } finally {
        setItemsLoaded(true);
      }
    };

    loadItems();
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('solsazon_recent_searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch {
        setRecentSearches([]);
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
    } else {
      setQuery('');
      setResults([]);
      setActiveIndex(-1);
    }
  }, [isOpen]);

  const saveRecentSearch = useCallback((term: string) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('solsazon_recent_searches', JSON.stringify(updated));
  }, [recentSearches]);

  const searchItems = useCallback((searchQuery: string) => {
    const q = normalize(searchQuery);
    if (!q) { setResults([]); return; }

    const filtered = allItems.filter((item) => {
      return matchesLocalizedSearch([item.name, item.category], q, language);
    });

    setResults(filtered.slice(0, 8));
    setActiveIndex(-1);
  }, [allItems, language]);

  useEffect(() => {
    const timer = setTimeout(() => searchItems(query), 200);
    return () => clearTimeout(timer);
  }, [query, searchItems]);

  const handleNavigate = (slug: string, name: string) => {
    saveRecentSearch(name);
    onClose();
    navigate(slug);
  };

  const handleSearchSubmit = () => {
    if (activeIndex >= 0 && activeIndex < results.length) {
      const result = results[activeIndex];
      gaSearch({ search_term: query, results_count: results.length, source: 'autocomplete' });
      handleNavigate(result.slug, result.name);
    } else if (query.trim()) {
      gaSearch({ search_term: query, results_count: results.length, source: 'search_bar' });
      saveRecentSearch(query);
      onClose();
      navigate(`/products?search=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    if (activeIndex >= 0 && resultsRef.current) {
      const activeEl = resultsRef.current.children[activeIndex] as HTMLElement;
      activeEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('solsazon_recent_searches');
  };

  const highlightMatch = (source: string, q: string) => {
    const text = tr(source);
    if (!q.trim()) return text;
    const normalized = normalize(q);
    const textNorm = normalize(text);
    const idx = textNorm.indexOf(normalized);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="text-forest-600 font-semibold">{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  // Productos destacados (nuevos) para el estado vacío
  const trendingProducts = allItems.filter(i => i.type === 'product').slice(0, 3);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[90] flex justify-center pt-0">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fadeIn" />

      {/* Search Container */}
      <div
        ref={containerRef}
        className="relative w-full max-w-2xl mx-4 mt-[72px] animate-slideDown"
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
            <div className="w-5 h-5 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-lg"></i>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={tr("Buscar productos, especias, packs...")}
              className="flex-1 font-inter text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
              autoComplete="off"
            />
            {tr(query && (
              <button
                onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            ))}
            <button
              onClick={onClose}
              className="font-inter text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer px-2 py-1 rounded-md bg-gray-50 whitespace-nowrap"
            >{tr("ESC")}</button>
          </div>

          {/* Results Area */}
          <div className="max-h-[60vh] overflow-y-auto">
            {/* Search Results */}
            {tr(query.trim() && results.length > 0 && (
              <div className="py-2">
                <div className="px-5 py-2">
                  <span className="font-inter text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    {tr(results.length)}{' '}{tr(results.length !== 1 ? 'resultados' : 'resultado')}
                  </span>
                </div>
                <div ref={resultsRef}>
                  {tr(results.map((result, index) => (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleNavigate(result.slug, result.name)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`w-full flex items-center gap-4 px-5 py-3 transition-colors cursor-pointer text-left ${
                        activeIndex === index ? 'bg-forest-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                        <ProductImage
                          src={result.image}
                          alt={tr(result.name)}
                          productName={result.name}
                          category={result.category}
                          className="w-full h-full object-cover object-top"
                          fallbackClassName="w-full h-full"
                          lazy={false}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-inter text-sm font-medium text-gray-800 truncate">
                            {tr(highlightMatch(result.name, query))}
                          </p>
                          {tr(result.type === 'bundle' && (
                            <span className="flex-shrink-0 bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-inter font-semibold whitespace-nowrap">{tr("PACK")}</span>
                          ))}
                        </div>
                        <p className="font-inter text-xs text-gray-400 mt-0.5">{tr(result.category)}</p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span className="font-inter text-sm font-bold text-forest-600">
                          &euro;{tr(result.price)}
                        </span>
                        {tr(result.isOnSale && result.originalPrice && (
                          <span className="block font-inter text-[11px] text-gray-400 line-through">
                            &euro;{tr(result.originalPrice)}
                          </span>
                        ))}
                      </div>
                    </button>
                  )))}
                </div>
                <div className="px-5 py-3 border-t border-gray-100">
                  <button
                    onClick={handleSearchSubmit}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-forest-50 hover:bg-forest-100 text-forest-700 font-inter text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >{tr("Ver todos los resultados para “")}{tr(query)}&rdquo;
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </div>
                  </button>
                </div>
              </div>
            ))}

            {/* No results */}
            {tr(query.trim() && results.length === 0 && (
              <div className="py-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 rounded-full bg-gray-100">
                  <i className="ri-search-line text-2xl text-gray-300"></i>
                </div>
                <p className="font-inter text-sm font-medium text-gray-500">{tr("No se encontraron resultados")}</p>
                <p className="font-inter text-xs text-gray-400 mt-1">{tr("Prueba con otros términos de búsqueda")}</p>
              </div>
            ))}

            {/* Default state */}
            {tr(!query.trim() && (
              <div className="py-3">
                {tr(recentSearches.length > 0 && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between px-5 py-2">
                      <span className="font-inter text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{tr("Búsquedas recientes")}</span>
                      <button onClick={clearRecentSearches} className="font-inter text-[11px] text-gray-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap">{tr("Borrar")}</button>
                    </div>
                    {tr(recentSearches.map((term, i) => (
                      <button key={i} onClick={() => setQuery(term)} className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer text-left">
                        <div className="w-5 h-5 flex items-center justify-center text-gray-300"><i className="ri-time-line text-base"></i></div>
                        <span className="font-inter text-sm text-gray-600">{tr(term)}</span>
                      </button>
                    )))}
                  </div>
                ))}

                <div>
                  <div className="px-5 py-2">
                    <span className="font-inter text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{tr("Categorías populares")}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 px-5 py-2 pb-4">
                    {tr(popularCategories.map((cat) => (
                      <button key={cat.label} onClick={() => setQuery(cat.label)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-50 hover:bg-forest-50 border border-gray-200 hover:border-forest-300 text-gray-600 hover:text-forest-700 transition-all cursor-pointer">
                        <div className="w-4 h-4 flex items-center justify-center"><i className={`${cat.icon} text-sm`}></i></div>
                        <span className="font-inter text-xs font-medium whitespace-nowrap">{tr(cat.label)}</span>
                      </button>
                    )))}
                  </div>
                </div>

                {tr(trendingProducts.length > 0 && (
                  <div className="border-t border-gray-100">
                    <div className="px-5 py-2 pt-3">
                      <span className="font-inter text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{tr("Productos destacados")}</span>
                    </div>
                    {tr(trendingProducts.map((product) => (
                      <button key={product.id} onClick={() => handleNavigate(product.slug, product.name)} className="w-full flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors cursor-pointer text-left">
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          <ProductImage src={product.image} alt={tr(product.name)} productName={product.name} category={product.category} className="w-full h-full object-cover object-top" fallbackClassName="w-full h-full" lazy={false} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-inter text-sm font-medium text-gray-700 truncate">{tr(product.name)}</p>
                        </div>
                        <span className="font-inter text-sm font-bold text-forest-600 flex-shrink-0">&euro;{tr(product.price)}</span>
                      </button>
                    )))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideDown { animation: slideDown 0.25s ease-out; }
      `}</style>
    </div>
  );
};

export default SearchAutocomplete;
